import React, { useEffect, useState } from "react";
import {
    Label,
    Input,
    Button,
    Field,
    Textarea,
    Text,
    Dropdown,
    Option
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import "../CSS/VariableForm.css";
const VariableForm = (props) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const [dbJsonText, setDbJsonText] = useState("");

    useEffect(() => {
        if (props.variable?.DB) {
            setDbJsonText(JSON.stringify(props.variable.DB, null, 2));
        } else {
            setDbJsonText("");
        }
    }, [props.variable?.id]);

    useEffect(() => {
        if (props.variable) {
            validateForm();
        }
    }, [props.variable?.displayName, props.variable?.scope, props.variable?.DB]);

    // Fonction pour compter les clés d'un objet (non récursive)
    const countKeys = (obj) => {
        if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
            return 0;
        }
        return Object.keys(obj).length;
    };

    // Fonction pour vérifier la profondeur d'un objet
    // Commence à 0 pour l'objet racine, donc niveau max accepté = 2
    const getObjectDepth = (obj, currentDepth = 0) => {
        if (typeof obj !== 'object' || obj === null) {
            return currentDepth;
        }

        // Si c'est un array, vérifier la profondeur des objets qu'il contient
        if (Array.isArray(obj)) {
            let maxDepth = currentDepth;
            for (const item of obj) {
                if (typeof item === 'object' && item !== null) {
                    const depth = getObjectDepth(item, currentDepth);
                    maxDepth = Math.max(maxDepth, depth);
                }
            }
            return maxDepth;
        }

        // C'est un objet - compter ce niveau
        let maxDepth = currentDepth;
        for (const value of Object.values(obj)) {
            if (typeof value === 'object' && value !== null) {
                const depth = getObjectDepth(value, currentDepth + 1);
                maxDepth = Math.max(maxDepth, depth);
            }
        }

        return maxDepth;
    };

    const validateForm = () => {
        if (!props.variable) return;

        const newErrors = {};

        if (!props.variable.displayName)
            newErrors.displayName = t("THIS_FIELD_IS_REQUIRED");
        if (!props.variable.scope) newErrors.scope = t("THIS_FIELD_IS_REQUIRED");

        // Validation DB
        const db = props.variable.DB;
         if (!db) newErrors.db = t("THIS_FIELD_IS_REQUIRED");
        console.log("Validating DB:", db);
        if (db && typeof db === 'object') {
            // Vérifier que customData existe et est un objet (OBLIGATOIRE)
            if (!db.customData || typeof db.customData !== 'object' || Array.isArray(db.customData)) {
                newErrors.db = t("CUSTOMDATA_REQUIRED");
            } else {
                // Vérifier la profondeur (max 2 niveaux)
                const depth = getObjectDepth(db);
                if (depth > 2) {
                    newErrors.db = t("DB_MAX_2_LEVELS");
                }

                // Vérifier le nombre de clés au niveau racine de DB (max 100)
                const dbRootKeys = countKeys(db);
                if (dbRootKeys > 100) {
                    newErrors.db = t("DB_MAX_100_KEYS");
                }

                // Vérifier le nombre de clés dans customData (max 100)
                const customDataKeys = countKeys(db.customData);
                if (customDataKeys > 100) {
                    newErrors.db = t("CUSTOMDATA_MAX_100_KEYS");
                }

                // Vérifier que les valeurs sont des primitives
                // Note: typeof null === "object" en JavaScript, mais null est accepté
                for (const [key, value] of Object.entries(db.customData)) {
                    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
                        newErrors.db = t("CUSTOMDATA_ONLY_PRIMITIVES");
                        break;
                    }
                }
            }
        }

        setErrors(newErrors);

        const isValid = Object.keys(newErrors).length === 0;
        if (props.setFormValid) props.setFormValid(isValid);
    };

    const handleDbJsonChange = (e) => {
        const newJsonText = e.target.value;
        setDbJsonText(newJsonText);

        // Essayer de parser le JSON
        try {
            // Vérifier les clés dupliquées au niveau racine
            const findDuplicateKeys = (text) => {
                const keys = [];
                let depth = 0;
                let inString = false;
                let currentKey = '';
                let capturingKey = false;
                
                for (let i = 0; i < text.length; i++) {
                    const char = text[i];
                    const prevChar = i > 0 ? text[i - 1] : '';
                    
                    // Gérer les chaînes
                    if (char === '"' && prevChar !== '\\') {
                        if (!inString) {
                            inString = true;
                            if (depth === 1) {
                                capturingKey = true;
                                currentKey = '';
                            }
                        } else {
                            inString = false;
                            if (capturingKey && depth === 1) {
                                // Vérifier si c'est suivi de ":"
                                let j = i + 1;
                                while (j < text.length && /\s/.test(text[j])) j++;
                                if (text[j] === ':') {
                                    keys.push(currentKey);
                                }
                                capturingKey = false;
                            }
                        }
                        continue;
                    }
                    
                    if (inString) {
                        if (capturingKey) {
                            currentKey += char;
                        }
                        continue;
                    }
                    
                    // Compter la profondeur
                    if (char === '{') depth++;
                    else if (char === '}') depth--;
                }
                
                // Trouver les duplicates
                const seen = new Set();
                const duplicates = new Set();
                keys.forEach(key => {
                    if (seen.has(key)) {
                        duplicates.add(key);
                    }
                    seen.add(key);
                });
                
                return Array.from(duplicates);
            };
            
            const duplicates = findDuplicateKeys(newJsonText);
            if (duplicates.length > 0) {
                setErrors(prev => ({ ...prev, db: t("DUPLICATE_KEYS_ERROR") + ": " + duplicates.join(", ") }));
                if (props.setFormValid) props.setFormValid(false);
                return;
            }

            const parsed = JSON.parse(newJsonText);

            // Vérifier que c'est un objet
            if (typeof parsed !== 'object' || Array.isArray(parsed)) {
                setErrors(prev => ({ ...prev, db: t("DB_MUST_BE_OBJECT") }));
                if (props.setFormValid) props.setFormValid(false);
                return;
            }

            // Vérifier que customData existe et est un objet
            if (!parsed.customData || typeof parsed.customData !== 'object' || Array.isArray(parsed.customData)) {
                setErrors(prev => ({ ...prev, db: t("CUSTOMDATA_REQUIRED") }));
                if (props.setFormValid) props.setFormValid(false);
                return;
            }

            // Mettre à jour la variable
            props.setVariable((prev) => ({
                ...prev,
                DB: parsed,
            }));

            // Effacer l'erreur DB pour laisser validateForm faire la validation complète
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.db;
                return newErrors;
            });

            // La validation complète se fera dans useEffect
        } catch (error) {
            setErrors(prev => ({ ...prev, db: t("INVALID_JSON") }));
            if (props.setFormValid) props.setFormValid(false);
        }
    };
    return (
        <div className="variable-form-container">
            <div className="form-section-variable">


                <Field
                    label={t("DISPLAY_NAME")}
                    required
                    fontweight="semibold"
                    validationMessage={errors.displayName}
                    validationState={errors.displayName ? "error" : undefined}
                >
                    <Input
                        className="variable-form"
                        value={props.variable?.displayName || ""}
                        placeholder={t("ENTER_DISPLAY_NAME")}
                        onChange={(e) =>
                            props.setVariable((prev) => ({
                                ...prev,
                                displayName: e.target.value,
                            }))
                        }
                    />
                </Field>

                <Field
                    label={t("SCOPE")}
                    required
                    fontweight="semibold"
                    validationMessage={errors.scope}
                    validationState={errors.scope ? "error" : undefined}
                >
                    <Dropdown
                        className=" variable-form"
                        placeholder={t("SELECT_ENTITY")} 
                        value={props.variable?.scope || ""}
                        selectedOptions={[props.variable?.scope || ""]}
                        onOptionSelect={(event, data) =>
                            props.setVariable((prev) => ({
                                ...prev,
                                scope: data.optionValue,
                            }))
                        }
                    >
                        {props.SCOPES_NAMES?.map((scope) => (
                            <Option key={scope} value={scope}>
                                {scope}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>
            </div>

            <div className="form-section-variable">
                

                <Field
                    label={t("DB_JSON")}
                    required
                    fontweight="semibold"
                    validationMessage={errors.db}
                    validationState={errors.db ? "error" : undefined}
                >
                    <Textarea
                        value={dbJsonText}
                        onChange={handleDbJsonChange}
                        rows={15}
                        resize="vertical"
                        className="db-json-textarea variable-form"
                        placeholder={`{\n  "key1": "value1",\n  "customData": {\n    "key1": "value1",\n    "key2": 123\n  }\n}`}
                    />
                </Field>

                <div className="validation-info">
                    <Text size={200} className="validation-info-text">
                        ✓ {t("DB_MAX_2_LEVELS_INFO")}<br />
                        ✓ {t("DB_MAX_100_KEYS_INFO")}<br />
                        ✓ {t("CUSTOMDATA_MAX_100_KEYS_INFO")}<br />
                        ✓ {t("CUSTOMDATA_PRIMITIVES_INFO")}
                    </Text>
                </div>
            </div>
        </div>
    );
};

export default VariableForm;
